/* v8 ignore file */
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { AuthGuard } from '@/components/layout/AuthGuard';
import { ErrorBoundary } from '@/components/layout/ErrorBoundary';
// Pages — lazy-loaded for code splitting
import { lazy, Suspense } from 'react';

const UiLiveDashboardPage = lazy(() => import('@/pages/UiLiveDashboardPage'));
const LiveDashboardPage = lazy(() => import('@/pages/LiveDashboardPage'));

const LoginPage = lazy(() => import('@/pages/LoginPage'));
const AuthCallbackPage = lazy(() => import('@/pages/AuthCallbackPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const TestPlansPage = lazy(() => import('@/pages/TestPlansPage'));
const AdminPage = lazy(() => import('@/pages/AdminPage'));
const PlanEditorPage = lazy(() => import('@/pages/PlanEditorPage'));
const RunsPage = lazy(() => import('@/pages/RunsPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const EnvironmentsPage = lazy(() => import('@/pages/EnvironmentsPage'));
const AlertsPage = lazy(() => import('@/pages/AlertsPage'));
const SchedulesPage = lazy(() => import('@/pages/SchedulesPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const RunComparePage = lazy(() => import('@/pages/RunComparePage'));
const LoadProfilesPage = lazy(() => import('@/pages/LoadProfilesPage'));
const TemplatesPage = lazy(() => import('@/pages/TemplatesPage'));
const BaselinePage = lazy(() => import('@/pages/BaselinePage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const AuditLogsPage = lazy(() => import('@/pages/AuditLogsPage'));
const McpPage = lazy(() => import('@/pages/McpPage'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-full min-h-[200px]">
      <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Force remount when navigating between different runs so state resets completely.
function LiveDashboardKeyed() {
  const { runId } = useParams<{ runId: string }>();
  return <LiveDashboardPage key={runId} />;
}

function UiLiveDashboardKeyed() {
  const { runId } = useParams<{ runId: string }>();
  return <UiLiveDashboardPage key={runId} />;
}

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public route */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />

        {/* Protected routes — wrapped in AppShell */}
        <Route element={<AuthGuard />}>
          <Route element={<AppShell />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/plans" element={<TestPlansPage />} />
            <Route path="/plans/new" element={<PlanEditorPage />} />
            <Route path="/plans/:id/edit" element={<PlanEditorPage />} />
            <Route path="/runs" element={<RunsPage />} />
            <Route path="/runs/compare" element={<RunComparePage />} />
            <Route
              path="/runs/:runId/live"
              element={
                <ErrorBoundary>
                  <LiveDashboardKeyed />
                </ErrorBoundary>
              }
            />
            <Route
              path="/runs/:runId/ui-live"
              element={
                <ErrorBoundary>
                  <UiLiveDashboardKeyed />
                </ErrorBoundary>
              }
            />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/baseline" element={<BaselinePage />} />
            <Route path="/environments" element={<EnvironmentsPage />} />
            <Route path="/alerts" element={<AlertsPage />} />
            <Route path="/schedules" element={<SchedulesPage />} />
            <Route path="/load-profiles" element={<LoadProfilesPage />} />
            <Route path="/templates" element={<TemplatesPage />} />
            <Route path="/mcp" element={<McpPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/audit-logs" element={<AuditLogsPage />} />
          </Route>
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
